import { alignColumns, type DiffChange } from "@gaurussel/tiptap-diff-utility";
import { Node } from "prosemirror-model";
import type { EditorState, Transaction } from "prosemirror-state";

const isRow = (node: Node | undefined): node is Node => node?.type.spec.tableRole === "row";

type Range = { from: number; to: number };

const inTable = (change: DiffChange, table: Range) =>
	change.posB !== undefined && change.posB >= table.from && change.posB <= table.to;

const narrowedRows = (change: DiffChange): [Node, Node] | undefined => {
	const oldRow = change.nodeA as Node | undefined;
	const newRow = change.nodeB as Node | undefined;
	if (change.type !== "modified" || !isRow(oldRow) || !isRow(newRow)) return undefined;
	return oldRow.childCount > newRow.childCount ? [oldRow, newRow] : undefined;
};

const keptOf = (rows: Array<[Node, Node]>): number[] | null => {
	const pairs = alignColumns(rows);
	return pairs.length === rows[0][1].childCount ? pairs.map(([oldIndex]) => oldIndex) : null;
};

// For every row of the table that lost cells, the old index of each of its new cells. Rows are
// lined up together, as the diff engine does: one row alone cannot tell which cell went away.
const keptCellsIn = (changes: DiffChange[], table: Range): Map<DiffChange, number[]> => {
	const groups = new Map<string, DiffChange[]>();
	changes.forEach((change) => {
		const rows = inTable(change, table) ? narrowedRows(change) : undefined;
		if (!rows) return;
		const group = `${rows[0].childCount}|${rows[1].childCount}`;
		groups.set(group, [...(groups.get(group) ?? []), change]);
	});
	const kept = new Map<DiffChange, number[]>();
	groups.forEach((group) => {
		const cells = keptOf(group.map((change) => narrowedRows(change) as [Node, Node]));
		if (cells) group.forEach((change) => kept.set(change, cells));
	});
	return kept;
};

export const isCellsOnlyDeletion = (change: DiffChange): boolean => {
	const rows = narrowedRows(change);
	const kept = rows && keptOf([rows]);
	return !!kept && kept.every((oldIndex, newIndex) => rows[0].child(oldIndex).eq(rows[1].child(newIndex)));
};

export const restoreDeletedColumn = (
	state: EditorState,
	changes: DiffChange[],
	table: { from: number; to: number },
	column: number,
): Transaction | null => {
	const tr = state.tr;
	keptCellsIn(changes, table).forEach((kept, change) => {
		const oldRow = change.nodeA as Node;
		const newRow = change.nodeB as Node;
		if (change.posB === undefined || column >= oldRow.childCount || kept.includes(column)) return;

		let at = change.posB + 1;
		kept.forEach((oldIndex, newIndex) => {
			if (oldIndex < column) at += newRow.child(newIndex).nodeSize;
		});
		const cell = Node.fromJSON(state.schema, oldRow.child(column).toJSON());
		tr.insert(tr.mapping.map(at), cell);
	});
	return tr.docChanged ? tr : null;
};

export const deletedColumnsIn = (changes: DiffChange[], table: Range): Set<number> => {
	const columns = new Set<number>();
	keptCellsIn(changes, table).forEach((kept, change) => {
		const oldRow = change.nodeA as Node;
		for (let index = 0; index < oldRow.childCount; index++) if (!kept.includes(index)) columns.add(index);
	});
	return columns;
};

const tableAround = (state: EditorState, pos: number): Range | undefined => {
	const Pos = state.doc.resolve(pos);
	for (let depth = Pos.depth; depth >= 0; depth--) {
		if (Pos.node(depth).type.spec.tableRole === "table")
			return depth === 0 ? undefined : { from: Pos.before(depth), to: Pos.after(depth) };
	}
	return undefined;
};

// An old row still has the cells of a column deleted since; put back as is it widens the table and
// prosemirror-tables pads every other row with empty cells.
export const fitRowsToTable = (state: EditorState, changes: DiffChange[], at: number, nodes: Node[]): Node[] => {
	if (!nodes.every(isRow) || at < 0 || at > state.doc.content.size) return nodes;
	const table = tableAround(state, at);
	if (!table) return nodes;
	const columns = deletedColumnsIn(changes, table);
	if (!columns.size) return nodes;
	return nodes.map((row) => {
		const cells: Node[] = [];
		row.forEach((cell, _, index) => {
			if (!columns.has(index)) cells.push(cell);
		});
		return row.type.create(row.attrs, cells, row.marks);
	});
};
